import React from "react";
import "./App.css";
import {
  contactPageData,
  degrees,
  experience,
  greeting,
  skills,
  socialMediaLinks,
} from "./data";

const work = experience.sections.flatMap(
  (section) => section.experiences || []
);

function Arrow() {
  return <span aria-hidden="true">↗</span>;
}

function App() {
  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="wordmark" href="#top" aria-label="Nitesh Purohit, home">
          NP<span>.</span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#expertise">Expertise</a>
          <a href="#experience">Experience</a>
          <a href="#contact">Contact</a>
        </nav>
      </header>

      <main id="top">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="eyebrow">Software engineer · Brampton, Canada</p>
            <h1 id="hero-title">
              I build systems that <em>stay built.</em>
            </h1>
            <p className="hero-intro">{greeting.subTitle}</p>
            <div className="hero-actions">
              <a className="button button-primary" href="#contact">
                Start a conversation <Arrow />
              </a>
              <a
                className="button button-secondary"
                href={greeting.resumeLink}
                target="_blank"
                rel="noreferrer"
              >
                View résumé
              </a>
            </div>
          </div>
          <div className="hero-aside" aria-label="Profile summary">
            <span className="availability-dot" aria-hidden="true" />
            <p>
              Currently building thoughtful products and dependable
              infrastructure.
            </p>
            <dl>
              <div>
                <dt>Focus</dt>
                <dd>Product engineering</dd>
              </div>
              <div>
                <dt>Strength</dt>
                <dd>Systems thinking</dd>
              </div>
              <div>
                <dt>Experience</dt>
                <dd>15+ years</dd>
              </div>
            </dl>
          </div>
        </section>

        <section
          className="section"
          id="expertise"
          aria-labelledby="expertise-title"
        >
          <div className="section-heading">
            <p className="section-number">01</p>
            <div>
              <p className="eyebrow">What I do</p>
              <h2 id="expertise-title">From product idea to production.</h2>
            </div>
          </div>
          <div className="expertise-grid">
            {skills.data.map((area) => (
              <article className="expertise-card" key={area.title}>
                <h3>{area.title}</h3>
                <ul>
                  {area.skills.map((item) => (
                    <li key={item}>{item.replace(/^⚡\s*/, "")}</li>
                  ))}
                </ul>
                <div
                  className="skill-list"
                  aria-label={`${area.title} technologies`}
                >
                  {area.softwareSkills.map((skill) => (
                    <span key={skill.skillName}>{skill.skillName}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          className="section"
          id="experience"
          aria-labelledby="experience-title"
        >
          <div className="section-heading">
            <p className="section-number">02</p>
            <div>
              <p className="eyebrow">Where I have worked</p>
              <h2 id="experience-title">Experience shaped by ownership.</h2>
            </div>
          </div>
          <div className="timeline">
            {work.map((job) => (
              <article className="job" key={`${job.company}-${job.duration}`}>
                <p className="job-duration">{job.duration}</p>
                <div>
                  <h3>{job.title}</h3>
                  <a href={job.company_url} target="_blank" rel="noreferrer">
                    {job.company} <Arrow />
                  </a>
                </div>
                <div>
                  <p>{job.description}</p>
                  <p className="job-location">{job.location}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          className="section education"
          aria-labelledby="education-title"
        >
          <div className="section-heading">
            <p className="section-number">03</p>
            <div>
              <p className="eyebrow">Foundation</p>
              <h2 id="education-title">Education</h2>
            </div>
          </div>
          {degrees.degrees.map((degree) => (
            <a
              className="education-card"
              href={degree.website_link}
              target="_blank"
              rel="noreferrer"
              key={`${degree.title}-${degree.subtitle}-${degree.duration}`}
            >
              <div>
                <p>{degree.duration}</p>
                <h3>{degree.subtitle}</h3>
                <span>{degree.title}</span>
                {degree.grade && (
                  <p className="education-grade">
                    <strong>Grade</strong>
                    {degree.grade}
                  </p>
                )}
                {degree.descriptions && degree.descriptions.length > 0 && (
                  <ul className="education-descriptions">
                    {degree.descriptions.map((description) => (
                      <li key={description}>
                        {description.replace(/^⚡\s*/, "")}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Arrow />
            </a>
          ))}
        </section>

        <section
          className="contact"
          id="contact"
          aria-labelledby="contact-title"
        >
          <p className="eyebrow">Have a problem worth solving?</p>
          <h2 id="contact-title">Let’s make something useful.</h2>
          <p>{contactPageData.contactSection.description}</p>
          <a
            className="contact-email"
            href="mailto:nitesh.purohit.it@gmail.com"
          >
            nitesh.purohit.it@gmail.com <Arrow />
          </a>
          <div className="social-links">
            {socialMediaLinks.map((social) => (
              <a
                href={social.link}
                target={
                  social.link.startsWith("mailto:") ? undefined : "_blank"
                }
                rel="noreferrer"
                key={social.name}
              >
                {social.name}
              </a>
            ))}
            <a
              href={contactPageData.blogSection.link}
              target="_blank"
              rel="noreferrer"
            >
              Blog
            </a>
          </div>
        </section>
      </main>

      <footer>
        <span>© {new Date().getFullYear()} Nitesh Purohit</span>
        <a href="#top">Back to top ↑</a>
      </footer>
    </div>
  );
}

export default App;
